import React, { useContext, useEffect, Suspense } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';

/**
 * GroupApprovalGate
 *
 * Global, pre-render gate for /company/* routes. For GROUP users whose account
 * is not APPROVED (pending, in_review, needs_info, rejected), the gate forces
 * the user to /company/settings and dispatches the UnderReviewModal event so
 * they see the admin's reason and required action.
 *
 * Admins pass through. Settings is always allowed so groups can correct flagged
 * fields. The settings flow will auto-transition needs_info → in_review when
 * they save corrections.
 */
const CoveragePendingImports = React.lazy(() => import('../../features/partner-coverage/CoveragePendingImports'));
const ALLOWED_PATHS = ['/company/settings', '/company/mcp-publication'];

/**
 * The review status this gate is allowed to act on, or null when we do not
 * know it yet.
 *
 * UNKNOWN IS A THIRD STATE AND MUST NOT READ AS "NOT APPROVED". `auth.user` is
 * seeded from the JWT alone in both AuthContext entry points (login and
 * loadAuthState), and that payload carries no `group` at all. So
 * `group.reviewStatus` is absent before /auth/me lands, and stays absent for
 * the whole session after a transient /auth/me failure, which AuthContext
 * deliberately survives on the token-only user rather than logging out. Reading
 * that absence as non-approved bounced APPROVED groups to
 * /company/settings?section=company with nothing on the page to fix and no way
 * back, because the redirect is one-way and replaces the history entry.
 *
 * The localStorage record AuthContext writes after every successful /auth/me is
 * the synchronous fallback. It is the same record the request blocklist in
 * api.js reads, so the gate and the blocker cannot disagree about who is
 * parked, and it is why an approved group that has loaded once stays approved
 * through a later /auth/me outage.
 */
const resolveReviewStatus = (user) => {
  const fromProfile = user?.group?.reviewStatus;
  if (fromProfile) return fromProfile;
  try {
    const raw = localStorage.getItem('groupApprovalCache');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.role !== 'GROUP') return null;
    return parsed.reviewStatus || null;
  } catch {
    return null;
  }
};

const GroupApprovalGate = ({ children }) => {
  const { auth } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  const role = auth?.user?.role;
  const isGroup = role === 'GROUP';
  const status = isGroup ? resolveReviewStatus(auth?.user) : null;
  // Positive knowledge only: a status we actually read, that is not approved.
  // An unresolved status leaves the user where they are — the backend's
  // GroupApprovedGuard is the boundary that actually refuses the work, and this
  // gate is the affordance that explains it.
  const isBlocked = isGroup && !!status && status !== 'approved';
  const coverageReceipt = ['/company/pipeline', '/company/listings'].includes(location.pathname);
  const pathAllowed = coverageReceipt || ALLOWED_PATHS.some((p) => location.pathname.startsWith(p));

  // Redirect non-approved group users to /company/settings. Do NOT dispatch the
  // UnderReviewModal event here — the modal has its own proactive mount effect
  // that respects sessionStorage dismissal. Dispatching here would re-open the
  // modal after the user clicked "Update Company Details" (which navigates to
  // settings with query params and therefore re-fires this effect).
  useEffect(() => {
    if (!isBlocked) return;
    if (!pathAllowed) {
      navigate('/company/settings?section=company' + (status === 'needs_info' ? '&highlight=needsInfo' : ''), {
        replace: true,
      });
    }
  }, [isBlocked, pathAllowed, status, location.pathname, navigate]);

  // While a non-approved group user is on a gated path, render nothing (so the
  // underlying page never mounts and never fires API calls). Once the redirect
  // lands on /company/settings, pathAllowed flips true and children render.
  if (isBlocked && !pathAllowed) {
    return null;
  }

  if (isBlocked && coverageReceipt) {
    return <Suspense fallback={<p>Loading your saved coverage…</p>}><CoveragePendingImports token={auth.token} listings={location.pathname === '/company/listings'} /></Suspense>;
  }

  return children;
};

export default GroupApprovalGate;
